import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { TaskDraft } from './taskDraftStore';

/**
 * A draft whose board was deleted while it waited (spec 121 FR-014). There is
 * nowhere left to create it, so the only ways on are to take the text
 * elsewhere or let it go. Closing folds it back into its chip, like any draft.
 */
export function MissingBoardDialog({
  draft,
  onClose,
  onDiscard,
}: {
  draft: TaskDraft;
  onClose: () => void;
  onDiscard: () => void;
}) {
  const [copied, setCopied] = useState<'yes' | 'refused' | null>(null);
  const text = [draft.title, draft.description]
    .filter((part) => part.trim() !== '')
    .join('\n\n');
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Board no longer exists</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          The board this task was started on has been deleted, so it can no
          longer be created there. Copy the text to start it on another board.
        </p>
        {text ? (
          <p className="max-h-48 overflow-y-auto text-sm whitespace-pre-wrap">
            {text}
          </p>
        ) : null}
        <p role="status" className="text-muted-foreground text-xs">
          {copied === 'yes'
            ? 'Copied.'
            : copied === 'refused'
              ? 'The text could not be copied — your browser refused the clipboard.'
              : null}
        </p>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            className="mr-auto"
            onClick={onDiscard}
          >
            Discard
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!text}
            onClick={() => {
              navigator.clipboard.writeText(text).then(
                () => {
                  setCopied('yes');
                },
                () => {
                  setCopied('refused');
                },
              );
            }}
          >
            Copy text
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
